import React, { useRef, useState } from 'react';
import {
  ActionButton,
  ToastQueue,
  Tooltip,
  TooltipTrigger
} from '@adobe/react-spectrum';
import ExportPdfIcon from 'app/assets/svgIcons/ExportPdfIcon';
import { SideNavTooltip } from './SideNavTooltip';
import headerStyles from './Header/Header.module.scss';
import styles from './SlideOutPanel.module.scss';

interface ExportPdfButtonProps {
  /** Prepares the live slideout and opens the browser print dialog. */
  onExport: () => Promise<void> | void;
  /** Header toolbar (beside clone) or legacy side-nav placement. */
  variant?: 'header' | 'sideNav';
}

/**
 * Prints the live slideout HTML. This preserves the UI, selectable text,
 * and real anchor links in the resulting PDF.
 */
export const ExportPdfButton: React.FC<ExportPdfButtonProps> = ({
  onExport,
  variant = 'sideNav'
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [openTooltip, setOpenTooltip] = useState(false);
  const tooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      await onExport();
    } catch (error) {
      console.error('Failed to export slideout PDF:', error);
      ToastQueue.negative('Unable to export PDF', { timeout: 5000 });
    } finally {
      setIsExporting(false);
    }
  };

  const handleTooltipMouseEnter = () => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }
    tooltipTimerRef.current = setTimeout(() => {
      setOpenTooltip(true);
    }, 1000);
  };

  const handleTooltipMouseLeave = () => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }
    setOpenTooltip(false);
  };

  if (variant === 'header') {
    return (
      <TooltipTrigger
        isOpen={openTooltip}
        onOpenChange={isOpen => {
          if (!isOpen) setOpenTooltip(false);
        }}
        placement="bottom"
      >
        <div
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
          style={{ display: 'contents' }}
        >
          <ActionButton
            onPress={handleExport}
            aria-label="Export PDF"
            data-testid="slide-out-header-export-pdf-button"
            UNSAFE_className={headerStyles.shareButton}
            isQuiet
            isDisabled={isExporting}
          >
            <ExportPdfIcon width={16} height={16} />
          </ActionButton>
        </div>
        <Tooltip>Export PDF</Tooltip>
      </TooltipTrigger>
    );
  }

  return (
    <SideNavTooltip label="Export PDF">
      <button
        type="button"
        className={styles.exportPdfButton}
        onClick={handleExport}
        onMouseDown={e => e.stopPropagation()}
        aria-label="Export PDF"
        disabled={isExporting}
        data-testid="slide-out-side-nav-export-pdf-button"
        style={{
          opacity: isExporting ? 0.5 : 1,
          cursor: isExporting ? 'not-allowed' : 'pointer'
        }}
      >
        <ExportPdfIcon width={20} height={20} />
      </button>
    </SideNavTooltip>
  );
};

export default ExportPdfButton;
